import { useTranslation } from "react-i18next"

import {
  AlertTriangle,
  Ban,
  BookOpen,
  Calendar,
  CheckCircle2,
  ExternalLink,
  FileWarning,
  HandHelping,
  Info,
  ShieldAlert,
  UserSearch,
} from "@/components/icons"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"
import { cn } from "@/lib/utils"

export function EUAIActPage() {
  const { i18n, t } = useTranslation("", { keyPrefix: "training.security.eu_ai_act" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))
  const timelineItems = list<{ date: string; date_time: string; label: string }>("timeline.items")
  const requestedLanguage = (i18n.resolvedLanguage ?? i18n.language).split("-")[0]
  const euLanguage = ["de", "fi", "sv"].includes(requestedLanguage) ? requestedLanguage : "en"
  const eurLexLanguage = euLanguage.toUpperCase()
  const sourceLinks = [
    {
      key: "final_text",
      href: `https://data.consilium.europa.eu/doc/document/PE-30-2026-REV-1/${euLanguage}/pdf`,
    },
    {
      key: "council",
      href: "https://www.consilium.europa.eu/en/press/press-releases/2026/06/29/artificial-intelligence-council-gives-final-green-light-to-simplify-and-streamline-rules/",
    },
    {
      key: "procedure",
      href: `https://eur-lex.europa.eu/procedure/${eurLexLanguage}/2025_359`,
    },
    {
      key: "base_act",
      href: `https://eur-lex.europa.eu/legal-content/${eurLexLanguage}/TXT/?uri=CELEX:32024R1689`,
    },
    {
      key: "broader_omnibus",
      href: `https://eur-lex.europa.eu/procedure/${eurLexLanguage}/2025_360`,
    },
    {
      key: "norway_status",
      href: "https://www.regjeringen.no/no/aktuelt/send-innspel-til-kva-eit-hogrisiko-ki-system-skal-vere-innan-23.-juni/id3161303/",
    },
  ] as const

  return (
    <CourseLayout>
      {/* Intro */}
      <CourseSection>
        <h1>{t("title")}</h1>
        <p className="text-lg">{t("intro")}</p>
      </CourseSection>

      {/* Current legal status */}
      <CourseSection>
        <div className="bg-muted/50 flex items-start gap-3 rounded-lg border p-5">
          <Info aria-hidden="true" className="text-muted-foreground mt-0.5 h-6 w-6 shrink-0" />
          <div className="space-y-2">
            <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              {t("status.eyebrow")}
            </p>
            <h2 className="text-lg font-bold">{t("status.title")}</h2>
            <p className="text-sm">{t("status.text")}</p>
            <p className="text-muted-foreground text-sm">{t("status.scope_note")}</p>
          </div>
        </div>
      </CourseSection>

      {/* AI literacy obligation */}
      <CourseSection>
        <Card className="border-border bg-muted/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BookOpen aria-hidden="true" className="h-5 w-5" />
              <h2 className="text-lg font-bold">{t("ai_literacy.title")}</h2>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">{t("ai_literacy.text")}</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 aria-hidden="true" className="text-success mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("ai_literacy.context")}</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle2 aria-hidden="true" className="text-success mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("ai_literacy.individual")}</span>
              </li>
            </ul>
            <p className="bg-background/70 rounded-lg border p-3 text-sm">
              {t("ai_literacy.caveat")}
            </p>
          </CardContent>
        </Card>
      </CourseSection>

      {/* Timeline */}
      <CourseSection>
        <h2 className="text-xl font-bold">{t("timeline.title")}</h2>
        <ol className="mt-4 grid gap-3 sm:grid-cols-2">
          {timelineItems.map((item, i) => (
            <li
              key={`${item.date}-${i}`}
              className="bg-muted/50 flex items-start gap-3 rounded-lg border p-4"
            >
              <Calendar
                aria-hidden="true"
                className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0"
              />
              <div>
                <time className="text-sm font-semibold" dateTime={item.date_time}>
                  {item.date}
                </time>
                <p className="text-muted-foreground mt-1 text-xs">{item.label}</p>
              </div>
            </li>
          ))}
        </ol>
      </CourseSection>

      {/* Three cards: Prohibited / High Risk / Do Instead */}
      <CourseSection>
        <h2 className="sr-only">{t("guidance_title")}</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Prohibited */}
          <Card className="border-destructive bg-destructive/15">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Ban aria-hidden="true" className="text-destructive h-5 w-5" />
                <h3 className="text-destructive text-base font-bold">{t("prohibited.title")}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2.5">
                {list("prohibited.items").map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Ban
                      aria-hidden="true"
                      className="text-destructive mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* High Risk */}
          <Card className="border-warning-ink/40 bg-warning/30">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <ShieldAlert aria-hidden="true" className="text-warning-ink h-5 w-5" />
                <h3 className="text-warning-ink text-base font-bold">{t("high_risk.title")}</h3>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2.5">
                {list("high_risk.items").map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <AlertTriangle
                      aria-hidden="true"
                      className="text-warning-ink mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="border-warning-ink/40 border-t pt-3 text-xs">{t("high_risk.note")}</p>
            </CardContent>
          </Card>

          {/* Do Instead */}
          <Card className="border-success bg-success/15">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 aria-hidden="true" className="text-success h-5 w-5" />
                <h3 className="text-success text-base font-bold">{t("do_instead.title")}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2.5">
                {list("do_instead.items").map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2
                      aria-hidden="true"
                      className="text-success mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </CourseSection>

      {/* Stop Rule callout */}
      <CourseSection>
        <div
          className={cn(
            "flex items-start gap-3 rounded-lg border p-5",
            "border-warning-ink/40 bg-warning/30"
          )}
        >
          <FileWarning aria-hidden="true" className="text-warning-ink mt-0.5 h-6 w-6 shrink-0" />
          <div>
            <h2 className="text-warning-ink text-base font-bold">{t("stop_rule.title")}</h2>
            <p className="mt-1 text-sm">{t("stop_rule.text")}</p>
            <p className="text-muted-foreground mt-2 text-xs italic">{t("stop_rule.keywords")}</p>
          </div>
        </div>
      </CourseSection>

      {/* CV / Recruitment specific note */}
      <CourseSection>
        <div className="flex items-start gap-3 rounded-lg border p-5">
          <UserSearch
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 h-6 w-6 shrink-0"
          />
          <div>
            <h2 className="text-base font-semibold">{t("cv_note.title")}</h2>
            <p className="mt-1 text-sm">{t("cv_note.text")}</p>
          </div>
        </div>
      </CourseSection>

      {/* Escalation reminder */}
      <CourseSection>
        <div className="bg-primary/5 border-primary/20 flex items-start gap-3 rounded-lg border p-5">
          <HandHelping aria-hidden="true" className="text-primary mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <h2 className="text-base font-semibold">{t("escalate.title")}</h2>
            <p className="mt-1 text-sm">{t("escalate.text")}</p>
          </div>
        </div>
      </CourseSection>

      {/* Official sources */}
      <CourseSection>
        <div className="space-y-3 border-t pt-6">
          <h2 className="text-lg font-bold">{t("sources.title")}</h2>
          <p className="text-muted-foreground text-sm">{t("sources.text")}</p>
          <ul className="space-y-2 text-sm">
            {sourceLinks.map((source) => (
              <li key={source.key}>
                <a
                  className="hover:text-primary inline-flex min-h-10 items-center gap-2 py-2 underline decoration-current/30 underline-offset-4"
                  href={source.href}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t(`sources.${source.key}`)}
                  <span className="sr-only">({t("sources.opens_new_tab")})</span>
                  <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </CourseSection>
    </CourseLayout>
  )
}
